// auto-generated by Typewriter template _models.tst from PortCall.EndPoints\Api\Domain\ApiDomain.cs
import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import TZDate from 'lib/TZDate';
import { jsonableObservable } from 'lib/jsonableObservable';
import { AutomationSystem, AutomationSystemData, AutomationSystemOptions } from 'data/models/AutomationSystem';
import { BerthCapability, BerthCapabilityData, BerthCapabilityOptions } from 'data/models/BerthCapability';
import { Coordination, CoordinationData, CoordinationOptions } from 'data/models/Coordination';
import { DomainRelevance, DomainRelevanceData, DomainRelevanceOptions } from 'data/models/DomainRelevance';
import { DomainSettings, DomainSettingsData, DomainSettingsOptions } from 'data/models/DomainSettings';
import { GeoActivity, GeoActivityData, GeoActivityOptions } from 'data/models/GeoActivity';
import { Organization, OrganizationData, OrganizationOptions } from 'data/models/Organization';
import { Port, PortData, PortOptions } from 'data/models/Port';

// DomainData is the class that Domain serializes to/from
export interface DomainData {
    Id : number;
	
    AutomationSystems : AutomationSystemData[] | null;
    BerthCapabilities : BerthCapabilityData[] | null;
    BerthCapabilitiesUrl : string | null;
    Coordinations : CoordinationData[] | null;
    CoordinationsUrl : string | null;
    DisplayableUrl : string;
    FrontEndRootUrl : string;
    GeoActivities : GeoActivityData[] | null;
    Organizations : OrganizationData[] | null;
    Ports : PortData[] | null;
    Settings : DomainSettingsData | null;
    Stream : DomainRelevanceData[] | null;
	
    HomePageSubtitle : string | null;
    HomePageTitle : string | null;
    Name : string;
    SystemEmailAddress : string;
    TimeZone : string;
}

// DomainOptions is the class which may be passed to the Domain constructor to define optional initial data values
export interface DomainOptions {
    Id : number;
	
    AutomationSystems? : AutomationSystemOptions[] | null;
    BerthCapabilities? : BerthCapabilityOptions[] | null;
    BerthCapabilitiesUrl? : string | null;
    Coordinations? : CoordinationOptions[] | null;
    CoordinationsUrl? : string | null;
    DisplayableUrl? : string;
    FrontEndRootUrl? : string;
    GeoActivities? : GeoActivityOptions[] | null;
    Organizations? : OrganizationOptions[] | null;
    Ports? : PortOptions[] | null;
    Settings? : DomainSettingsOptions | null;
    Stream? : DomainRelevanceOptions[] | null;
	
    HomePageSubtitle? : string | null;
    HomePageTitle? : string | null;
    Name? : string;
    SystemEmailAddress? : string;
    TimeZone? : string;
}

export class Domain {
    public Id : DataSignal<number>;
	
    public AutomationSystems : SDataArray<AutomationSystem>;
    public BerthCapabilities : SDataArray<BerthCapability>;
    public BerthCapabilitiesUrl : DataSignal<string | null>;
    public Coordinations : SDataArray<Coordination>;
    public CoordinationsUrl : DataSignal<string | null>;
    public DisplayableUrl : DataSignal<string>;
    public FrontEndRootUrl : DataSignal<string>;
    public GeoActivities : SDataArray<GeoActivity>;
    public Organizations : SDataArray<Organization>;
    public Ports : SDataArray<Port>;
    public Settings : DataSignal<DomainSettings | null>;
    public Stream : SDataArray<DomainRelevance>;
	
    public HomePageSubtitle : DataSignal<string | null>;
    public HomePageTitle : DataSignal<string | null>;
    public Name : DataSignal<string>;
    public SystemEmailAddress : DataSignal<string>;
    public TimeZone : DataSignal<string>;

    constructor();
    constructor(data : DomainOptions);
    constructor(data: DomainOptions | null = null) {
        this.Id = jsonableObservable(S.value<number>(data && data.Id !== undefined ? data.Id : 0));
	    
        this.AutomationSystems = SArray<AutomationSystem>([]);
        this.BerthCapabilities = SArray<BerthCapability>([]);
        this.BerthCapabilitiesUrl = jsonableObservable(S.value<string | null>(data && data.BerthCapabilitiesUrl !== undefined ? data.BerthCapabilitiesUrl : null));
        this.Coordinations = SArray<Coordination>([]);
        this.CoordinationsUrl = jsonableObservable(S.value<string | null>(data && data.CoordinationsUrl !== undefined ? data.CoordinationsUrl : null));
        this.DisplayableUrl = jsonableObservable(S.value<string>(data && data.DisplayableUrl !== undefined ? data.DisplayableUrl : ''));
        this.FrontEndRootUrl = jsonableObservable(S.value<string>(data && data.FrontEndRootUrl !== undefined ? data.FrontEndRootUrl : ''));
        this.GeoActivities = SArray<GeoActivity>([]);
        this.Organizations = SArray<Organization>([]);
        this.Ports = SArray<Port>([]);
        this.Settings = S.value<DomainSettings | null>(null);
        this.Stream = SArray<DomainRelevance>([]);
	    
        this.HomePageSubtitle = jsonableObservable(S.value<string | null>(data && data.HomePageSubtitle !== undefined ? data.HomePageSubtitle : null));
        this.HomePageTitle = jsonableObservable(S.value<string | null>(data && data.HomePageTitle !== undefined ? data.HomePageTitle : null));
        this.Name = jsonableObservable(S.value<string>(data && data.Name !== undefined ? data.Name : ''));
        this.SystemEmailAddress = jsonableObservable(S.value<string>(data && data.SystemEmailAddress !== undefined ? data.SystemEmailAddress : ''));
        this.TimeZone = jsonableObservable(S.value<string>(data && data.TimeZone !== undefined ? data.TimeZone : ''));
    }

    static key(obj : Domain) {
        return obj.Id();
    }
    
    static dataKey(obj : DomainOptions) {
        return obj.Id;
    }

    static update(obj : Domain, data : DomainOptions) {
        S.freeze(() => {
            if (data.Id !== undefined) obj.Id(data.Id);
	        
            if (data.BerthCapabilitiesUrl !== undefined) obj.BerthCapabilitiesUrl(data.BerthCapabilitiesUrl);
            if (data.CoordinationsUrl !== undefined) obj.CoordinationsUrl(data.CoordinationsUrl);
            if (data.DisplayableUrl !== undefined) obj.DisplayableUrl(data.DisplayableUrl);
            if (data.FrontEndRootUrl !== undefined) obj.FrontEndRootUrl(data.FrontEndRootUrl);
	        
            if (data.HomePageSubtitle !== undefined) obj.HomePageSubtitle(data.HomePageSubtitle);
            if (data.HomePageTitle !== undefined) obj.HomePageTitle(data.HomePageTitle);
            if (data.Name !== undefined) obj.Name(data.Name);
            if (data.SystemEmailAddress !== undefined) obj.SystemEmailAddress(data.SystemEmailAddress);
            if (data.TimeZone !== undefined) obj.TimeZone(data.TimeZone);
        });
    }
}



// WEBPACK FOOTER //
// ./server/models/Domain.ts